"use client"
import { ConfigProvider, Form, Input, message, Modal, Select, Button, InputNumber, DatePicker } from "antd";
import { Dispatch, SetStateAction, useEffect, useState, useCallback } from "react";
import container from "@/lib/di/container";
import React from "react";
import { WaterRegistrationModel } from "@/core/models/water-registration/water-registration.model";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { UnitTypeModel } from "@/core/models/unit-type/unit-type.model";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { mapWaterRegistrationToContract, findTollAreaByLocation } from "../utils/mapping.utils";
import { contractStatusPipe } from "@/core/pipes/contract-status.pipe";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { DEF_DATE_FORMAT } from "@/app/home/utils/global.utils";
import { SyncOutlined } from "@ant-design/icons";

const contractRepo = container.get(ContractRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const tollAreaRepo = container.get(TollAreaRepo);

interface CreateContractModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    registrationData: WaterRegistrationModel,
    onSuccess?: () => void,
}

const INPUT_WIDTH = 450;

export const CreateContractModal: React.FC<CreateContractModalProps> = ({
    isModalOpen, 
    setIsModalOpen, 
    registrationData,
    onSuccess
}) => {
    const [form] = Form.useForm<ContractModel>();
    const [loading, setLoading] = useState<boolean>(false);
    const [unitTypes, setUnitTypes] = useState<UnitTypeModel[]>([]);
    const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
    const [isLoadingTollArea, setIsLoadingTollArea] = useState<boolean>(false);

    const init = async () => {
        const [_unitTypes, _tollAreas] = await Promise.all([
            unitTypeRepo.getAll(),
            tollAreaRepo.getAll()
        ]);
        setUnitTypes(_unitTypes);
        setTollAreas(_tollAreas);
    };

    const populateFormData = useCallback(async () => {
        // Map data from WaterRegistration to Contract
        const mappedData = mapWaterRegistrationToContract(registrationData);
        
        // Try to find TollAreaId from location
        if (registrationData.provinceId && registrationData.districtId && registrationData.wardId) {
            setIsLoadingTollArea(true);
            const tollArea = await findTollAreaByLocation(
                registrationData.provinceId,
                registrationData.districtId,
                registrationData.wardId
            );
            if (tollArea) {
                mappedData.tollAreaId = tollArea.id;
                // Auto-generate code if TollAreaId is found
                try {
                    const codePayload = await contractRepo.generateCode(tollArea.id);
                    if (codePayload) {
                        mappedData.code = codePayload.code;
                        mappedData.identityCode = codePayload.identityCode;
                    }
                } catch (err) {
                    console.log("Error generating code:", err);
                }
            }
            setIsLoadingTollArea(false);
        }

        // Set form values
        form.setFieldsValue(mappedData);
    }, [registrationData, form]);

    useEffect(() => {
        if (isModalOpen) {
            init();
            populateFormData();
        }
    }, [isModalOpen, populateFormData]);

    const handleUnitTypeChange = (recordId: string) => {
        // UnitType change handler - có thể thêm logic khác nếu cần
    };

    const generateCode = async () => {
        const tollAreaId = form.getFieldValue("tollAreaId");
        if (tollAreaId) {
            const payload = await contractRepo.generateCode(tollAreaId);
            if (payload) {
                form.setFieldsValue({
                    "code": payload.code,
                    "identityCode": payload.identityCode
                });
            } else {
                message.error("Xảy ra lỗi khi tạo mã!");
            }
        } else {
            message.error("Chưa chọn khu vực!");
        }
    };

    const resetCode = async () => {
        form.setFieldsValue({
            "code": undefined,
            "identityCode": undefined
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            
            // Prepare data for creation
            const contractData: Partial<ContractModel> = {
                ...formData,
                waterFactoryId: registrationData.waterFactoryId,
            };

            const result = await contractRepo.create(contractData as ContractModel);
            setIsModalOpen(false);
            if (result) {
                message.success({
                    content: "Tạo hợp đồng thành công"
                });
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                message.error({
                    content: "Tạo hợp đồng thất bại"
                });
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        form.resetFields();
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={1000} 
                style={{ top: 20 }}
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={false} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">TẠO HỢP ĐỒNG TỪ ĐĂNG KÝ</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<ContractModel>
                        name="basic"
                        className="flex flex-col"
                        autoComplete="off"
                        labelAlign="right"
                        layout="vertical"
                        form={form}
                    >
                        <p className="text-xl font-bold text-primary my-1">1. THÔNG TIN HỢP ĐỒNG</p>
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<ContractModel>
                                label="Người đại diện"
                                className="col-span-2"
                                name="name"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Input placeholder="Nhập Người đại diện" style={{width: "100%"}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Loại hình đơn vị"
                                name="unitTypeId"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Select placeholder="Chọn Loại hình đơn vị" style={{width: INPUT_WIDTH}} onChange={handleUnitTypeChange}>
                                    {unitTypes.map(opt => <Select.Option key={opt.id} value={opt.id}>{opt.name}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Khu vực"
                                name="tollAreaId"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Select
                                    showSearch
                                    loading={isLoadingTollArea}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={tollAreas.map(area => ({value: area.id, label: area.name}))}
                                    placeholder="Nhập Khu vực" 
                                    style={{width: INPUT_WIDTH}} 
                                    onChange={() => resetCode()}
                                />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Mã hợp đồng"
                                name="code"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Input 
                                    placeholder="Nhập Mã hợp đồng" 
                                    style={{width: INPUT_WIDTH}}
                                    addonAfter={
                                        <Button 
                                            type="text" 
                                            style={{height: 30, color: "var(--primary-color)"}} 
                                            icon={<SyncOutlined />} 
                                            onClick={generateCode}
                                        />
                                    }
                                />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                name="identityCode"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                hidden
                            >
                                <InputNumber 
                                    min={1}
                                    max={2}
                                />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Tài khoản đại diện"
                                name="userName"
                            >
                                <Input placeholder="Nhập Tài khoản đại diện" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Địa chỉ"
                                className="col-span-2"
                                name="address"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Input placeholder="Nhập Địa chỉ" style={{width: "100%"}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Trạng thái hoạt động"
                                name="status"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Select 
                                    placeholder="Đang đăng ký" 
                                    style={{width: INPUT_WIDTH}}
                                    disabled
                                >
                                    <Select.Option value="Processing">{contractStatusPipe("Processing")}</Select.Option>
                                </Select>
                            </Form.Item>
                        </div>
                        <div className="mt-1 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                        <p className="text-xl font-bold text-primary my-1">2. THÔNG TIN KHÁCH HÀNG</p>
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<ContractModel>
                                label="Tên đơn vị/ tổ chức"
                                name="organization"
                            >
                                <Input placeholder="Nhập Tên đơn vị/ tổ chức" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="CMND/CCCD"
                                name="identityNo"
                            >
                                <Input placeholder="Nhập CMND/CCCD" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Mã số thuế"
                                name="taxCode"
                            >
                                <Input placeholder="Nhập Mã số thuế" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Ngày cấp CCCD"
                                name="identityDate"
                                getValueProps={(i) => ({value: dayjsPipe(i)})}
                            >
                                <DatePicker 
                                    format={DEF_DATE_FORMAT}
                                    placeholder="Nhập Ngày cấp CCCD" 
                                    style={{width: INPUT_WIDTH}}
                                />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Điện thoại"
                                name="phone"
                            >
                                <Input placeholder="Nhập Điện thoại" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Email"
                                name="email"
                            >
                                <Input placeholder="Nhập Email" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Số tài khoản"
                                name="bankNo"
                            >
                                <Input placeholder="Nhập Số tài khoản" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Ngân hàng"
                                name="bank"
                            >
                                <Input placeholder="Nhập Ngân hàng" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                        </div>
                        <div className="mt-1 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                        <p className="text-xl font-bold text-primary my-1">3. THÔNG TIN ĐỒNG HỒ</p>
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<ContractModel>
                                label="Mã đồng hồ"
                                name="waterMeterCode"
                            >
                                <Input placeholder="Nhập Mã đồng hồ" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Tên đồng hồ"
                                name="waterMeterName"
                            >
                                <Input placeholder="Nhập Tên đồng hồ" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Loại đồng hồ"
                                name="waterMeterType"
                            >
                                <Input placeholder="Nhập Loại đồng hồ" style={{width: INPUT_WIDTH}} />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};
